import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Grid,
    Dialog,
    DialogContent,
    InputBase,
    IconButton,
    Divider,
    Typography,
    DialogActions,
    Button
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import axios from "axios";
import { parseSQL } from 'react-querybuilder';
import { normalizeOperator } from '../../methods';
import CircularProgress from '@mui/material/CircularProgress';

function AskAIModal({ openModal, handleCloseModal, setSnackbarInfo, setFilters, metadata }) {
    const [prompt, setPrompt] = useState('');
    const [loading, setLoading] = useState(false);
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("lg"));

    function combineRowsByColumn(objects) {
        const result = {};
        for (const obj of objects) {
            const column = obj.column;
            const rows = obj.rows;
            if (!result[column]) {
                result[column] = obj;
            } else {
                result[column].rows = result[column].rows.concat(rows);
            }
        }
        return Object.values(result);
    }

    const findKeyInObjectsArray = (objectsArray, keyToFind) => {
        for (let i = 0; i < objectsArray.length; i++) {
            const currentObject = objectsArray[i];
            if (currentObject.key === keyToFind || currentObject.column === keyToFind) {
                return currentObject;
            }
            if (currentObject.childFilters) {
                const result = findKeyInObjectsArray(currentObject.childFilters, keyToFind);
                if (result) {
                    return result;
                }
            }
        }
        return null;
    }

    const createFilterObj = (filter) => {
        const obj = {
            category: filter.category,
            friendlyName: filter.friendlyName,
            column: filter.column,
            key: filter.key,
            rows: [],
        };
        return obj;
    }

    const handleSubmitPrompt = async () => {
        try {
            setLoading(true);
            const initialContext = `-- Prompt for generating SQL queries based on natural language input
            -- Ensure that the output is SQL code without explanations, adhering to the following schema and rules:
            
            -- Table Schemas:
            DH_CRC_IUMED.IUMED_DBO_MEDICATION_HISTORY (DATA_CENTER_KEY, MEDICATION_KEY, DRUG_KEY, MEDICATION_ID, MEDICATION_CODE_SYSTEM, MEDICATION_CODE, MEDICATION_NAME, ROUTE_OF_ADMINISTRATION, UNITS_PER_ADMINISTRATION, FREQUENCY, STRENGTH)
            DH_CRC_IUMED.IUMED_DBO_PATIENT (DATA_CENTER_KEY, SEX, PATIENT_AGE, MARITAL_STATUS, RACE, ETHNICITY)
            DH_CRC_IUMED.IUMED_DBO_PROCEDURE_HISTORY (DATA_CENTER_KEY, PROCEDURE_CODE, PROCEDURE_DESCRIPTION, PROCEDURE_DATE)
            DH_CRC_IUMED.IUMED_DBO_DIAGNOSIS_HISTORY (DATA_CENTER_KEY, DIAGNOSIS_KEY, DIAGNOSIS_ID, DATA_CENTER_ID, DIAGNOSIS_CODE_SYSTEM, DIAGNOSIS_CODE, DIAGNOSIS_DESCRIPTION, REPORTED_DATE, DIAGNOSIS_DATE, DIAGNOSIS_STATUS)
            DH_CRC_IUMED.IUMED_DBO_EJECTION_FRACTION (DATA_CENTER_KEY, STUDY_DATE, DISPLAY_NAME, EFVALUE_MIN, EFVALUE_MAX)
            
            -- SQL Generation Rules:
            1. Do not use aliases when joining tables.
            2. Use 'or' instead of 'in' operator in the WHERE clause.
            3. If medication dosage is specified, use the STRENGTH column in the MEDICATION_HISTORY table.
            4. If medication frequency is mentioned, use the FREQUENCY column in the MEDICATION_HISTORY table.
            5. Include the schema and table name in front of all columns in the SELECT statement.
            6. Only join tables that are necessary to return relevant data.
            7. Do not use correlated subqueries.
            8. Use the 'LIKE' operator for pattern matching, utilizing the '%' wildcard for partial matches.
            9.) Do not uppercase the medication name
            
            -- Example Requests and Expected SQL Outputs:
            Request: Give me all patients over age 50 on aspirin 500mg twice daily.
            Expected SQL:
            SELECT DH_CRC_IUMED.IUMED_DBO_PATIENT.DATA_CENTER_KEY, DH_CRC_IUMED.IUMED_DBO_PATIENT.SEX, DH_CRC_IUMED.IUMED_DBO_PATIENT.PATIENT_AGE, DH_CRC_IUMED.IUMED_DBO_PATIENT.MARITAL_STATUS, DH_CRC_IUMED.IUMED_DBO_PATIENT.RACE, DH_CRC_IUMED.IUMED_DBO_PATIENT.ETHNICITY
            FROM DH_CRC_IUMED.IUMED_DBO_PATIENT
            INNER JOIN DH_CRC_IUMED.IUMED_DBO_MEDICATION_HISTORY
            ON DH_CRC_IUMED.IUMED_DBO_PATIENT.DATA_CENTER_KEY = DH_CRC_IUMED.IUMED_DBO_MEDICATION_HISTORY.DATA_CENTER_KEY
            WHERE DH_CRC_IUMED.IUMED_DBO_PATIENT.PATIENT_AGE > 50
            AND DH_CRC_IUMED.IUMED_DBO_MEDICATION_HISTORY.MEDICATION_NAME = 'aspirin'
            AND DH_CRC_IUMED.IUMED_DBO_MEDICATION_HISTORY.STRENGTH = '500mg'
            AND DH_CRC_IUMED.IUMED_DBO_MEDICATION_HISTORY.FREQUENCY = 'Twice daily';
            
            Request: Find Patients with an ejection fraction description like 'Transesophageal echocardiography' and medication name is not 'Entresto'.
            Expected SQL:
            SELECT DH_CRC_IUMED.IUMED_DBO_PATIENT.DATA_CENTER_KEY, DH_CRC_IUMED.IUMED_DBO_PATIENT.SEX, DH_CRC_IUMED.IUMED_DBO_PATIENT.PATIENT_AGE, DH_CRC_IUMED.IUMED_DBO_PATIENT.MARITAL_STATUS, DH_CRC_IUMED.IUMED_DBO_PATIENT.RACE, DH_CRC_IUMED.IUMED_DBO_PATIENT.ETHNICITY
            FROM DH_CRC_IUMED.IUMED_DBO_PATIENT
            INNER JOIN DH_CRC_IUMED.IUMED_DBO_EJECTION_FRACTION
            ON DH_CRC_IUMED.IUMED_DBO_PATIENT.DATA_CENTER_KEY = DH_CRC_IUMED.IUMED_DBO_EJECTION_FRACTION.DATA_CENTER_KEY
            INNER JOIN DH_CRC_IUMED.IUMED_DBO_MEDICATION_HISTORY
            ON DH_CRC_IUMED.IUMED_DBO_PATIENT.DATA_CENTER_KEY = DH_CRC_IUMED.IUMED_DBO_MEDICATION_HISTORY.DATA_CENTER_KEY
            WHERE DH_CRC_IUMED.IUMED_DBO_EJECTION_FRACTION.DISPLAY_NAME like 'Transesophageal echocardiography%'
            AND DH_CRC_IUMED.IUMED_DBO_MEDICATION_HISTORY.MEDICATION_NAME != 'Entresto';
            
            Request: Find Patients with a diagnosis code of I22.0 or I22.1 or I22.02 or I22.08 or I22.09.
            Expected SQL:
            SELECT DH_CRC_IUMED.IUMED_DBO_PATIENT.DATA_CENTER_KEY, DH_CRC_IUMED.IUMED_DBO_PATIENT.SEX, DH_CRC_IUMED.IUMED_DBO_PATIENT.PATIENT_AGE, DH_CRC_IUMED.IUMED_DBO_PATIENT.MARITAL_STATUS, DH_CRC_IUMED.IUMED_DBO_PATIENT.RACE, DH_CRC_IUMED.IUMED_DBO_PATIENT.ETHNICITY
            FROM DH_CRC_IUMED.IUMED_DBO_PATIENT
            INNER JOIN DH_CRC_IUMED.IUMED_DBO_DIAGNOSIS_HISTORY
            ON DH_CRC_IUMED.IUMED_DBO_PATIENT.DATA_CENTER_KEY = DH_CRC_IUMED.IUMED_DBO_DIAGNOSIS_HISTORY.DATA_CENTER_KEY
            WHERE DH_CRC_IUMED.IUMED_DBO_DIAGNOSIS_HISTORY.DIAGNOSIS_CODE = 'I22.0'
            OR DH_CRC_IUMED.IUMED_DBO_DIAGNOSIS_HISTORY.DIAGNOSIS_CODE = 'I22.1'
            OR DH_CRC_IUMED.IUMED_DBO_DIAGNOSIS_HISTORY.DIAGNOSIS_CODE = 'I22.02'
            OR DH_CRC_IUMED.IUMED_DBO_DIAGNOSIS_HISTORY.DIAGNOSIS_CODE = 'I22.08'
            OR DH_CRC_IUMED.IUMED_DBO_DIAGNOSIS_HISTORY.DIAGNOSIS_CODE = 'I22.09';
            
            -- Input the user's natural language request below to generate the corresponding SQL code:
          `
            const config = {
                headers: {
                    // Authorization: `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`
                    Authorization: `Bearer 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`

                }
            }
            setSnackbarInfo({
                open: true,
                message: 'Processing....',
                severity: "info",
            });
            const result = await axios.post('https://api.openai.com/v1/chat/completions',
                {
                    mode: 'gpt-3.5-turbo-0125',
                    temperature: 0,
                    messages: [
                        { role: "system", content: initialContext },
                        { role: "user", content: `Intrepret: '${prompt}'` }
                    ],

                },
                config
            )
            if (result?.status === 200) {
                let data = result?.data?.choices[0]?.message?.content;

                data = data.replaceAll('`', '');
                data = data.replaceAll('\n', ' ');

                if (data.includes('SELECT')) {
                    //regex pattern to grab operator from sql statement
                    const operatorRegex = /([=<>!]+)/;
                    const operatorMatch = data.match(operatorRegex)[1] || null;

                    const parsedSql = parseSQL(data);
                    const rules = parsedSql.rules;
                    let newFiltersArray = []
                    rules.forEach(item => {
                        const filter = findKeyInObjectsArray(metadata.queryItems, item.field);
                        if (filter !== null) {
                            const newFilterObj = createFilterObj(filter);
                            newFilterObj.rows = [{
                                field: item.field,
                                friendlyName: filter.friendlyName,
                                dataType: filter.dataType,
                                operator: normalizeOperator(item.operator),
                                value: item.value
                            }]
                            newFiltersArray.push(newFilterObj);
                        }
                    })
                    const combinedRows = combineRowsByColumn(newFiltersArray);
                    setFilters(combinedRows)
                }
                setLoading(false);
                handleCloseModal();
                setPrompt('');
                setSnackbarInfo({
                    open: true,
                    message: 'Filter Added!',
                    hideDuration: 3000,
                    severity: "success",
                });
            }
        }
        catch (err) {
            console.log({ err });
            setLoading(false);
            setSnackbarInfo({
                open: true,
                message: "Unable to create filter. Please try again or ask the question differently.",
                hideDuration: 3000,
                severity: "error",
            });
        }
    }


    return (
        <Dialog
            fullScreen={fullScreen}
            fullWidth
            maxWidth="lg"

            open={openModal}
            onClose={() => handleCloseModal()}
            aria-labelledby="ai-modal-title"
            aria-describedby="ai-modal-description"
        >
            <DialogContent sx={{ p: 3 }}>
                {loading ?
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center'
                    }}>
                        <CircularProgress />
                    </Box>
                    :
                    <Grid container spacing={3}>
                        <Grid size={12} >
                            <Box sx={{
                                backgroundColor: '#b7e3f2',
                                p: 3
                            }}>
                                <Typography sx={{
                                    marginBottom: '20px'
                                }}>Below is an example of how to prompt the AI based on your criteria:</Typography>
                                <ul>
                                    <li>
                                        Example 1:
                                        <Typography><strong>Criteria:</strong> Patients with STEMI or NSTEMI diagnosis</Typography>
                                        <Typography><strong>Prompt:</strong> Find Patients with a diagnosis code of I21 or diagnosis code of I21.0 or diagnosis code of I21.01</Typography>
                                    </li>
                                    <li>
                                        Example 2:
                                        <Typography><strong>Criteria:</strong> Patients with Subsequent STEMI or NSTEMI diagnosis</Typography>
                                        <Typography><strong>Prompt:</strong> Find Patients with a diagnosis code of I22.0 or diagnosis code of I22.1 or diagnosis code of I22.02 or diagnosis code of I22.08 or or diagnosis code of I22.09</Typography>
                                    </li>
                                    <li>
                                        Example 3:
                                        <Typography><strong>Criteria:</strong> Patients with a transesophageal echocardiography (procedure) without Entresto</Typography>
                                        <Typography><strong>Prompt:</strong> {"Find Patients with an ejection fraction description like 'Transesophageal echocardiography' and medication name is not 'Entresto'"}
                                        </Typography>
                                    </li>

                                </ul>
                            </Box>
                        </Grid>

                        <Grid size={12} >
                            <Box
                                sx={{
                                    p: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    backgroundColor: "surface.raised",
                                    border: "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                <InputBase
                                    fullWidth
                                    value={prompt}
                                    sx={{
                                        paddingLeft: '10px'
                                    }}
                                    placeholder="Ask RWE AI"
                                    onChange={(event) => setPrompt(event.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            handleSubmitPrompt();
                                        }
                                    }}
                                    endAdornment={
                                        prompt?.length > 0 && (
                                            <IconButton
                                                sx={{ p: "10px" }}
                                                aria-label="cancel"
                                                onClick={() => {
                                                    setPrompt('');
                                                }}
                                            >
                                                <CancelOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        )
                                    }
                                    size="small"
                                />
                                <Divider orientation="vertical" flexItem />
                                <IconButton
                                    sx={{
                                        p: 1,
                                        backgroundColor: 'rgb(24, 144, 255)',
                                        color: 'white',
                                        borderRadius: '0px'
                                    }}
                                    aria-label="search"
                                    size="small"
                                    onClick={() => {
                                        handleSubmitPrompt();
                                    }}
                                >
                                    <SearchIcon />
                                </IconButton>
                            </Box>
                        </Grid>
                    </Grid>
                }
            </DialogContent>
            <DialogActions sx={{
                borderTop: '1px solid lightgrey'
            }}>
                <Button
                    color="error"
                    sx={{ margin: "5px" }}
                    onClick={() => handleCloseModal()}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default AskAIModal